'use client';

import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit2, Check, X, Filter, CheckCircle, Circle, MoreVertical, Download } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { todoListContent } from '../../data/todo-list-content';

type Todo = {
  id: string;
  text: string;
  completed: boolean;
  createdAt: Date;
};

type FilterType = 'all' | 'active' | 'completed';

export default function TodoList() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const inputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Load todos from localStorage on component mount
  useEffect(() => {
    const savedTodos = localStorage.getItem('todos');
    if (savedTodos) {
      try {
        const parsedTodos = JSON.parse(savedTodos);
        // Convert string dates back to Date objects
        const todosWithDates = parsedTodos.map((todo: any) => ({
          ...todo,
          createdAt: new Date(todo.createdAt)
        }));
        setTodos(todosWithDates);
      } catch (error) {
        console.error('Failed to parse todos', error);
      }
    }
  }, []);

  // Save todos to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('todos', JSON.stringify(todos));
  }, [todos]);

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingId]);

  // Add a new todo
  const addTodo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const newTodo: Todo = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      completed: false,
      createdAt: new Date()
    };

    setTodos([newTodo, ...todos]);
    setInputValue('');
    inputRef.current?.focus();
  };

  // Toggle todo completion status
  const toggleTodo = (id: string) => {
    setTodos(todos.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  // Start editing a todo
  const startEditing = (id: string, text: string) => {
    setEditingId(id);
    setEditText(text);
  };

  // Save edited todo
  const saveEdit = (id: string) => {
    if (!editText.trim()) {
      setEditingId(null);
      return;
    }

    setTodos(todos.map(todo =>
      todo.id === id ? { ...todo, text: editText.trim() } : todo
    ));
    setEditingId(null);
  };

  // Delete a todo
  const deleteTodo = (id: string) => {
    setTodos(todos.filter(todo => todo.id !== id));
  };

  // Clear all completed todos
  const clearCompleted = () => {
    setTodos(todos.filter(todo => !todo.completed));
  };

  // Download todos as a text file
  const downloadTodos = () => {
    const date = new Date();
    const formattedDate = date.toISOString().split('T')[0];
    const fileName = `todo-list-${formattedDate}.txt`;

    // Format todos as text
    const todoText = todos.map(todo => {
      const status = todo.completed ? '✅' : '⬜';
      const dateStr = todo.createdAt.toLocaleDateString();
      return `${status} ${todo.text} (Created: ${dateStr})`;
    }).join('\n');

    // Create and trigger download
    const element = document.createElement('a');
    const file = new Blob([todoText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = fileName;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Filter todos based on the selected filter
  const filteredTodos = todos.filter(todo => {
    if (filter === 'active') return !todo.completed;
    if (filter === 'completed') return todo.completed;
    return true; // 'all'
  });

  // Count active todos
  const activeCount = todos.filter(todo => !todo.completed).length;

  // Format date as "X time ago"
  const formatDate = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-12 px-6 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <ToolPageIntro title="Todo List - Free Online Task Manager">
                Organize your tasks and boost productivity with our free online todo list. Create, edit, and manage your daily tasks with ease.
            </ToolPageIntro>
        </div>

        {/* Todo List Section - Centered */}
        <div className="flex justify-center mb-12">
          <div className="w-full max-w-2xl">
            {/* Add Todo Form */}
            <form onSubmit={addTodo} className="mb-6">
              <div className="flex shadow-lg rounded-lg overflow-hidden">
                <input
                  type="text"
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="What needs to be done?"
                  className="flex-1 px-6 py-4 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none"
                />
                <button
                  type="submit"
                  className="px-6 bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
                  aria-label="Add todo"
                >
                  <Plus size={24} />
                </button>
              </div>
            </form>

            {/* Todo List */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
              {/* Filter Tabs */}
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setFilter('all')}
                  className={`flex-1 py-3 text-sm font-medium ${filter === 'all'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('active')}
                  className={`flex-1 py-3 text-sm font-medium ${filter === 'active'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setFilter('completed')}
                  className={`flex-1 py-3 text-sm font-medium ${filter === 'completed'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  Completed
                </button>
              </div>

              {/* Todo Items */}
              {filteredTodos.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  {filter === 'all'
                    ? 'No tasks yet. Add one above!'
                    : filter === 'active'
                      ? 'No active tasks. Great job!'
                      : 'No completed tasks yet.'}
                </div>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredTodos.map(todo => (
                    <li
                      key={todo.id}
                      className="group hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center px-4 py-3">
                        {/* Toggle Complete */}
                        <button
                          onClick={() => toggleTodo(todo.id)}
                          className="p-1 mr-3 text-gray-400 hover:text-green-500 dark:text-gray-500 dark:hover:text-green-400 transition-colors"
                          aria-label={todo.completed ? 'Mark as incomplete' : 'Mark as complete'}
                        >
                          {todo.completed ? (
                            <CheckCircle className="w-6 h-6 text-green-500" />
                          ) : (
                            <Circle className="w-6 h-6" />
                          )}
                        </button>

                        {/* Todo Text / Edit Input */}
                        <div className="flex-1 min-w-0">
                          {editingId === todo.id ? (
                            <div className="flex items-center">
                              <input
                                ref={editInputRef}
                                type="text"
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit(todo.id);
                                  if (e.key === 'Escape') setEditingId(null);
                                }}
                                className="flex-1 bg-transparent border-b border-blue-500 py-1 px-2 text-gray-900 dark:text-white focus:outline-none"
                              />
                              <div className="flex ml-2">
                                <button
                                  type="button"
                                  onClick={() => saveEdit(todo.id)}
                                  className="p-1 text-green-500 hover:text-green-600 dark:hover:text-green-400"
                                  aria-label="Save changes"
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingId(null)}
                                  className="p-1 text-red-500 hover:text-red-600 dark:hover:text-red-400 ml-1"
                                  aria-label="Cancel editing"
                                >
                                  <X size={18} />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span
                                className={`text-gray-900 dark:text-white ${todo.completed ? 'line-through text-gray-400 dark:text-gray-500' : ''
                                  }`}
                              >
                                {todo.text}
                              </span>
                              <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <span className="text-xs text-gray-400 mr-2">
                                  {formatDate(todo.createdAt)}
                                </span>
                                <button
                                  onClick={() => startEditing(todo.id, todo.text)}
                                  className="p-1 text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 transition-colors"
                                  aria-label="Edit todo"
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  onClick={() => deleteTodo(todo.id)}
                                  className="p-1 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors ml-1"
                                  aria-label="Delete todo"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Todo Stats and Actions */}
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                <div className="mb-2 sm:mb-0">
                  {activeCount} {activeCount === 1 ? 'item left' : 'items left'}
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    onClick={downloadTodos}
                    disabled={todos.length === 0}
                    className="flex items-center px-3 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Download todos"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    <span>Download</span>
                  </button>
                  <button
                    onClick={clearCompleted}
                    className="flex items-center px-3 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!todos.some(todo => todo.completed)}
                    title="Clear completed todos"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    <span>Clear completed</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Keyboard Shortcuts Help */}
            <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Keyboard Shortcuts
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-center">
                  <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded mr-2">Enter</kbd>
                  <span>Add/Save todo</span>
                </div>
                <div className="flex items-center">
                  <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded mr-2">Esc</kbd>
                  <span>Cancel editing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informational Content */}
      <div className="mt-12">
        <InformationalContent
          title={todoListContent.title}
          description={todoListContent.description}
          content={todoListContent.content}
          faqs={todoListContent.faqs}
          relatedTools={todoListContent.relatedTools}
        />
      </div>
    </div>
  );
}
